import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { format } from 'date-fns';
import { Link, useHistory, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import ITableOrders from '~/interfaces/ITableOrders';

import Dashboard from '~/layouts/Dashboard';

import { useAuth } from '~/hooks/auth';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';

import { useOrders } from '~/hooks/orders';
import { useToast } from '~/hooks/toast';
import ICartProduct from '~/interfaces/ICartProduct';
import IOrder from '~/interfaces/IOrder';
import { bff } from '~/services/bff';
import { handlePrint } from '~/utils/handlePrint';
import TableClosingModal from './TableClosingModal';

interface Params {
  id: string;
}

const { REACT_APP_FIREBASE_PROJECT, REACT_APP_GROUP_TABLE_ORDERS } =
  process.env;

const OrderDetails: React.FC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const { id } = params;
  const { addToast } = useToast();
  const { company, user } = useAuth();
  const [order, setOrder] = useState({} as ITableOrders);
  const [peopleCount, setPeopleCount] = useState(1);

  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const { loadOrdersTable } = useOrders();

  const loadOrder = useCallback(async () => {
    if (id) {
      const response = await bff.get(
        `/tables-orders/${REACT_APP_FIREBASE_PROJECT}/${id}`,
      );

      const { tableOrder, orders } = response.data;

      const tmpOrder = orders.map((item: any) => {
        return { ...JSON.parse(item.order), item_id: item.id };
      });

      setOrder({
        ...tmpOrder[0],
        ...tableOrder,
        orders: tmpOrder,
        created_at_formatted: format(
          new Date(tableOrder.created_at),
          'dd/MM/yyyy HH:mm',
        ),
      });
    }
  }, [id]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const client = useMemo(() => {
    if (order && order.orders && order.orders.length > 0) {
      const { name, whatsapp } = order.orders[0];

      return { name, whatsapp };
    }
    return {};
  }, [order]);

  const products = useMemo(() => {
    let list: ICartProduct[] = [];

    if (!order || !order.orders) return [];

    order.orders.forEach(orderItem => {
      const parseList = orderItem.products.map(item => ({
        ...item,
        item_id: (orderItem as any).item_id,
      }));

      list = [...list, ...parseList];
    });

    console.log('REACT_APP_GROUP_TABLE_ORDERS', REACT_APP_GROUP_TABLE_ORDERS);

    if (REACT_APP_GROUP_TABLE_ORDERS === 'false') {
      return list;
    }

    const grouped: ICartProduct[] = [];

    list.forEach(item => {
      const findIndex = grouped.findIndex(
        findItem => findItem.uid === item.uid,
      );

      if (findIndex === -1) {
        grouped.push({ ...item });
      } else {
        grouped[findIndex].quantity += item.quantity;
      }
    });

    return grouped;
  }, [order]);

  const artisticCouvertTotal = useMemo(() => {
    if (!company.artistic_couvert_value || !peopleCount) return 0;
    return (company.artistic_couvert_value || 0) * peopleCount;
  }, [company.artistic_couvert_value, peopleCount]);

  const totalWithCouvert = useMemo(() => {
    return (order.total || 0) + artisticCouvertTotal;
  }, [order.total, artisticCouvertTotal]);

  const handlePrintCustom = useCallback(
    (event: any) => {
      event.preventDefault();

      const first = order.orders[0];
      const created_at = new Date(first.created_at);

      const parseOrder: IOrder = {
        ...first,
        total: totalWithCouvert,
        products,
        subtotal_formatted: getCurrencyFormatted(order.total),
        shippingCost_formatted: getCurrencyFormatted(0),
        total_formatted: getCurrencyFormatted(totalWithCouvert),
        created_at_formatted: format(created_at, 'dd/MM/yyyy HH:mm'),
        waiter_name: order.waiter_name,
        artisticCouvertTotal,
      };

      handlePrint({ order: parseOrder, company, cozinha: true });
    },
    [order, products, company, totalWithCouvert, artisticCouvertTotal],
  );

  const handlePrintParcial = useCallback(
    (event: any) => {
      event.preventDefault();

      const first = order.orders[0];
      const created_at = new Date(first.created_at);

      const parseOrder: IOrder = {
        ...first,
        total: totalWithCouvert,
        products,
        subtotal_formatted: getCurrencyFormatted(order.total),
        shippingCost_formatted: getCurrencyFormatted(0),
        total_formatted: getCurrencyFormatted(totalWithCouvert),
        created_at_formatted: format(created_at, 'dd/MM/yyyy HH:mm'),
        waiter_name: order.waiter_name,
        artisticCouvertTotal,
      };

      handlePrint({ order: parseOrder, company, parcial: true });
    },
    [order, products, company, totalWithCouvert, artisticCouvertTotal],
  );

  const handleConfirmeTableClose = useCallback(async () => {
    const item = order;

    const { isConfirmed } = await Swal.fire({
      title: 'Atenção!',
      text: 'Confirme o fechamento da conta?',
      icon: 'warning',
      confirmButtonText: 'Sim',
      showCancelButton: true,
      cancelButtonText: 'Não',
    });

    if (isConfirmed) {
      // await updateDoc(doc(DB, 'table_orders', item.uid), { status: 0 });

      await bff.patch(`/tables-orders/${REACT_APP_FIREBASE_PROJECT}/${id}`);
      await loadOrdersTable();

      addToast({
        type: 'success',
        description: `A conta da mesa ${item.table_name} foi fechada com sucesso!`,
      });

      history.goBack();
    }
  }, [addToast, order, history, id, loadOrdersTable]);

  const handleRemoveItem = useCallback(
    async (data: any) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do item?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
        preConfirm: async () => {
          await bff.delete(
            `/tables-orders/${REACT_APP_FIREBASE_PROJECT}/${data.item_id}/${data.id}`,
          );

          return loadOrder();
        },
      });
      // if (isConfirmed) {
      // await bff.delete(
      //   `/tables-orders/${REACT_APP_FIREBASE_PROJECT}/${data.item_id}/${data.id}`,
      // );
      // await loadOrder();
      // }
    },
    [loadOrder],
  );

  return (
    <Dashboard>
      <div className="nk-content-inner">
        <div className="nk-content-body">
          <div className="nk-block-head">
            <div className="nk-block-between g-3">
              <div className="nk-block-head-content">
                <h3 className="nk-block-title page-title">
                  {`Pedidos Mesa: ${order.table_name}`}
                </h3>
                <div className="nk-block-des text-soft">
                  <ul className="list-inline">
                    <li>
                      Data e Hora:
                      <span className="text-base">
                        {order.created_at_formatted}
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="nk-block-head-content">
                <Link
                  to="/table-orders"
                  className="btn btn-outline-light bg-white d-none d-sm-inline-flex"
                >
                  <em className="icon ni ni-arrow-left" />
                  <span>Voltar</span>
                </Link>
                <Link
                  to="/orders"
                  className="btn btn-icon btn-outline-light bg-white d-inline-flex d-sm-none"
                >
                  <em className="icon ni ni-arrow-left" />
                </Link>
              </div>
            </div>
          </div>
          <div className="nk-block">
            <div className="invoice">
              <div className="invoice-action d-flex flex-column align-items-end">
                <div className="d-flex flex-row">
                  {user.isAdmin && (
                    <button
                      type="button"
                      className="btn btn-icon btn-lg btn-white btn-dim btn-outline-primary mr-2"
                      onClick={handlePrintCustom}
                    >
                      <em className="icon ni ni-printer-fill" />
                    </button>
                  )}
                  {user.isAdmin && (
                    <button
                      type="button"
                      className="btn btn-lg btn-white btn-dim btn-outline-primary"
                      onClick={handlePrintParcial}
                    >
                      <em className="icon ni ni-printer-fill mr-2" />
                      Parcial
                    </button>
                  )}
                </div>
                {user.isAdmin && (
                  <button
                    type="button"
                    className="btn btn-primary mt-3"
                    onClick={() => setShowPaymentModal(true)}
                  >
                    Fechar Conta
                  </button>
                )}
              </div>
              <div className="invoice-wrap">
                <div>
                  <img
                    src={company.image}
                    alt="logo"
                    style={{ height: 150, objectFit: 'contain' }}
                  />
                </div>
                <div className="invoice-head mt-2">
                  <div className="invoice-contact">
                    <span className="overline-title">Cliente</span>
                    <div className="invoice-contact-info">
                      <h4 className="title">{client.name}</h4>
                      <ul className="list-plain">
                        {/* <li>
                          <em className="icon ni ni-map-pin-fill" />
                          <span>
                            {addressFormatted}
                            <br />
                          </span>
                          {order.address && order.address.complement && (
                            <span>{`, ${order.address.complement}`}</span>
                          )}
                          {order.address && order.address.reference && (
                            <span>{`, ${order.address.reference}`}</span>
                          )}
                        </li> */}
                        {client.whatsapp && (
                          <li>
                            <em className="icon ni ni-call-fill" />
                            <span>{client.whatsapp}</span>
                          </li>
                        )}
                        {order && order.waiter_name && (
                          <li>
                            {/* <em className="icon ni ni-call-fill" /> */}
                            <span className="overline-title">
                              ATENDIDO POR:
                            </span>
                            <h5>{order.waiter_name}</h5>
                          </li>
                        )}
                        {user.isAdmin && (
                          <li>
                            <span className="overline-title">
                              QUANTIDADE DE PESSOAS:
                            </span>
                            <div className="d-flex align-items-center mt-2">
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  border: '1px solid #e5e9f2',
                                  borderRadius: '8px',
                                  padding: '2px',
                                  background: '#fff',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  onClick={() => {
                                    if (peopleCount > 1) {
                                      setPeopleCount(peopleCount - 1);
                                    }
                                  }}
                                  disabled={peopleCount <= 1}
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '6px',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background:
                                      peopleCount <= 1 ? '#f5f6fa' : '#f0f4ff',
                                    border: 'none',
                                    color:
                                      peopleCount <= 1 ? '#cbd5e0' : '#4c6ef5',
                                    cursor:
                                      peopleCount <= 1
                                        ? 'not-allowed'
                                        : 'pointer',
                                    transition: 'all 0.2s ease',
                                  }}
                                  onMouseEnter={e => {
                                    if (peopleCount > 1) {
                                      e.currentTarget.style.background =
                                        '#e5edff';
                                    }
                                  }}
                                  onMouseLeave={e => {
                                    if (peopleCount > 1) {
                                      e.currentTarget.style.background =
                                        '#f0f4ff';
                                    }
                                  }}
                                >
                                  <em
                                    className="icon ni ni-minus"
                                    style={{ fontSize: '14px' }}
                                  />
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  className="form-control"
                                  style={{
                                    width: 70,
                                    textAlign: 'center',
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: '16px',
                                    fontWeight: 600,
                                    padding: '8px 4px',
                                    margin: '0 4px',
                                  }}
                                  value={peopleCount}
                                  onChange={event => {
                                    const count =
                                      parseInt(event.target.value, 10) || 1;
                                    setPeopleCount(count > 0 ? count : 1);
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  onClick={() => {
                                    setPeopleCount(peopleCount + 1);
                                  }}
                                  style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '6px',
                                    padding: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#f0f4ff',
                                    border: 'none',
                                    color: '#4c6ef5',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                  }}
                                  onMouseEnter={e => {
                                    e.currentTarget.style.background =
                                      '#e5edff';
                                  }}
                                  onMouseLeave={e => {
                                    e.currentTarget.style.background =
                                      '#f0f4ff';
                                  }}
                                >
                                  <em
                                    className="icon ni ni-plus"
                                    style={{ fontSize: '14px' }}
                                  />
                                </button>
                              </div>
                            </div>
                          </li>
                        )}

                        {/* {order.pix_file && (
                          <li>
                            <a
                              href={order.pix_file}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                textTransform: 'uppercase',
                                fontWeight: 'bold',
                              }}
                            >
                              Visualizar Comprovante Pix
                            </a>
                          </li>
                        )} */}
                      </ul>
                    </div>
                  </div>
                  <div className="invoice-desc">
                    <h3 className="title">Pedidos</h3>
                    {order.uid && (
                      <ul className="list-plain">
                        <li className="invoice-id">
                          <span className="text-danger">
                            {`Status: ${
                              order.status === 1 ? 'MESA OCUPADA' : 'MESA LIVRE'
                            }`}
                          </span>
                        </li>
                      </ul>
                    )}
                  </div>
                </div>
                <div className="invoice-bills">
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          {user.isAdmin && <th aria-label="Ações" />}
                          <th>Qtd</th>
                          <th>Item</th>
                          <th>Valor</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {products &&
                          products.map((product, index) => (
                            <>
                              <tr>
                                {user.isAdmin && (
                                  <td>
                                    <button
                                      onClick={() => handleRemoveItem(product)}
                                      type="button"
                                    >
                                      Excluir
                                    </button>
                                  </td>
                                )}

                                <td>
                                  {product.measure === 'KG'
                                    ? product.quantity.toFixed(3)
                                    : `${product.quantity}x`}
                                  {product.measure
                                    ? product.measure.toLocaleLowerCase()
                                    : ''}
                                </td>
                                <td>
                                  {`${product.name}`}

                                  {product.observations && (
                                    <span style={{ marginLeft: 8 }}>
                                      {`(Obs: ${product.observations})`}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  {getCurrencyFormatted(
                                    parseFloat(product.value),
                                  )}
                                </td>
                                <td>
                                  {getCurrencyFormatted(
                                    parseFloat(product.value) *
                                      product.quantity,
                                  )}
                                </td>
                              </tr>

                              {product.complements &&
                                product.complements.length > 0 && (
                                  <tr>
                                    <td
                                      colSpan={5}
                                      style={{
                                        fontSize: 10,
                                        textAlign: 'left',
                                      }}
                                    >
                                      {product.complements.map(complement => (
                                        <span style={{ marginRight: 8 }}>
                                          {`${complement.quantity}x - ${complement.item.name}`}
                                        </span>
                                      ))}
                                    </td>
                                  </tr>
                                )}
                            </>
                          ))}
                      </tbody>
                      <tfoot>
                        {/* <tr>
                          <td colSpan={2} />
                          <td colSpan={2}>Forma de pagamento</td>
                          <td>{PAYMENT_METHODS[order.paymentMethod]}</td>
                        </tr> */}
                        {/* {order.paymentMethod === '0' && (
                          <tr>
                            <td colSpan={2} />
                            <td colSpan={2}>Troco para</td>
                            <td>{order.thing || 'R$ 0,00'}</td>
                          </tr>
                        )} */}
                        {/* <tr>
                          <td colSpan={2} />
                          <td colSpan={2}>Subtotal</td>
                          <td>{getCurrencyFormatted(order.subtotal)}</td>
                        </tr> */}
                        {/* <tr>
                          <td colSpan={2} />
                          <td colSpan={2}>Taxa de entrega</td>
                          <td>{getCurrencyFormatted(order.shippingCost)}</td>
                        </tr> */}
                        {artisticCouvertTotal > 0 && (
                          <tr>
                            <td colSpan={3} />
                            <td>Couvert Artístico</td>
                            <td>
                              {getCurrencyFormatted(artisticCouvertTotal)}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan={3} />
                          <td>Total</td>
                          {order && (
                            <td>{getCurrencyFormatted(totalWithCouvert)}</td>
                          )}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <TableClosingModal
        show={showPaymentModal}
        total={totalWithCouvert}
        artisticCouvertTotal={artisticCouvertTotal}
        table={order.table_name}
        waiter_name={order.waiter_name}
        products={products}
        onClose={() => setShowPaymentModal(false)}
      />
    </Dashboard>
  );
};

export default OrderDetails;
